import { useCallback, useMemo, useState } from "react"
import { LegendTooltip } from "./LegendTooltip"
import { ChartDataPoint } from "./hooks/useBrandComparison"
import { ResponsiveItemList } from "./ResponsiveItemList"

interface CompanyInfo {
  name: string
  ai_generated: boolean
  color: string
  score: string
}

interface CustomLegendProps {
  selectedCategory: string
  hoveredCompany: string | null
  onHover: (company: string | null) => void
  data: ChartDataPoint[]
  currentCompanyName: string
}

export const CustomLegend = ({
  selectedCategory,
  hoveredCompany,
  onHover,
  data,
  currentCompanyName,
}: CustomLegendProps) => {
  const [tooltipPosition, setTooltipPosition] = useState<{
    x: number
    y: number
  } | null>(null)
  const [tooltipData, setTooltipData] = useState<{
    date: string
    currentCompany: CompanyInfo
    hoveredCompany: CompanyInfo
  } | null>(null)

  const latestData = useMemo(() => data?.[data.length - 1], [data])
  const currentCompanyInfo = useMemo(
    () => latestData?.companies?.[currentCompanyName],
    [latestData, currentCompanyName],
  )

  // Memoize companies array to prevent unnecessary re-renders
  const companies: CompanyInfo[] = useMemo(
    () =>
      latestData
        ? Object.entries(latestData.companies).map(([name, info]) => ({
            name,
            ...info,
            score: info.score.toString(),
          }))
        : [],
    [latestData],
  )

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent, company: CompanyInfo) => {
      if (company?.name === currentCompanyName) return

      const rect = event.currentTarget.getBoundingClientRect()
      const x = rect.left + rect.width / 2
      const y = rect.top

      // Get the most recent data point
      if (!latestData) return
      if (!currentCompanyInfo) return

      setTooltipPosition({ x, y })
      setTooltipData({
        date: latestData.date,
        currentCompany: {
          name: currentCompanyName,
          ...currentCompanyInfo,
          score: currentCompanyInfo.score.toString(),
        },
        hoveredCompany: company,
      })
      onHover(company?.name)
    },
    [currentCompanyName, latestData, currentCompanyInfo, onHover],
  )

  const handleMouseLeave = useCallback(() => {
    setTooltipPosition(null)
    setTooltipData(null)
    onHover(null)
  }, [onHover])

  // Split companies into AI and non-AI
  const nonAICompanies = useMemo(
    () => companies.filter(company => !company.ai_generated),
    [companies],
  )
  const aiCompanies = useMemo(
    () => companies.filter(company => company.ai_generated),
    [companies],
  )

  // Memoize renderCompany to prevent ResponsiveItemList from re-rendering
  const renderCompany = useCallback(
    (company: CompanyInfo) => (
      <div
        className={`flex items-center gap-system-xs px-system-sm py-system-xs rounded-lg transition-all duration-200 cursor-pointer
        ${company?.name === currentCompanyName ? "bg-white/10" : "hover:bg-white/5"}
        ${hoveredCompany && company?.name !== hoveredCompany && company?.name !== currentCompanyName ? "opacity-30" : "opacity-100"}
      `}
        onMouseEnter={e => handleMouseEnter(e, company)}
        onMouseLeave={handleMouseLeave}
      >
        <div className="flex items-center gap-system-xs">
          <div
            className={`w-system-xxs h-system-md rounded-full transition-all duration-200`}
            style={{
              backgroundColor: company?.color,
              boxShadow: `0 0 10px ${company?.color}40`,
            }}
          />
          <div className="flex items-center gap-system-xs">
            <span className="text-sm text-white/90 font-medium capitalize">
              {company?.name}
            </span>
            {company?.ai_generated && (
              <span className="text-[10px] font-medium bg-pink/20 text-pink-300 px-system-xs py-system-xxs rounded">
                AI
              </span>
            )}
          </div>
        </div>
      </div>
    ),
    [currentCompanyName, hoveredCompany, handleMouseEnter, handleMouseLeave],
  )

  // Memoize moreButtonContent to prevent ResponsiveItemList from re-rendering
  const moreButtonContent = useCallback(
    (count: number) => (
      <div className="flex items-center gap-system-xs">
        <span className="text-sm text-white/90">+{count} more</span>
      </div>
    ),
    [],
  )

  return (
    <>
      <div className="flex flex-row gap-system-lg">
        {/* Non-AI Companies Section */}
        {nonAICompanies.length > 0 && (
          <div className="flex flex-col gap-system-xs flex-1">
            <div className="flex items-center gap-system-xs">
              <span className="text-xs text-white/60 uppercase tracking-wider font-medium">
                Competitors
              </span>
              <div className="h-px bg-white/20 flex-1" />
            </div>
            <ResponsiveItemList
              items={nonAICompanies}
              renderItem={renderCompany}
              getKey={company => company?.name}
              containerClassName="items-center"
              moreButtonContent={moreButtonContent}
            />
          </div>
        )}

        {/* AI Companies Section */}
        {aiCompanies.length > 0 && (
          <div className="flex flex-col gap-system-xs flex-1">
            <div className="flex items-center gap-system-xs">
              <span className="text-xs text-white/60 uppercase tracking-wider font-medium">
                AI Suggested Brands
              </span>
              <div className="h-px bg-white/20 flex-1" />
            </div>
            <ResponsiveItemList
              items={aiCompanies}
              renderItem={renderCompany}
              getKey={company => company?.name}
              containerClassName="items-center"
              moreButtonContent={moreButtonContent}
            />
          </div>
        )}
      </div>

      {tooltipPosition && tooltipData && (
        <div
          style={{
            position: "fixed",
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y - 185}px`,
            zIndex: 999992,
            transform: "translateX(-50%)",
          }}
        >
          <LegendTooltip
            date={tooltipData.date}
            selectedCategory={selectedCategory}
            currentCompany={tooltipData.currentCompany}
            hoveredCompany={tooltipData.hoveredCompany}
          />
        </div>
      )}
    </>
  )
}
